<!-- Loading State -->
<div *ngIf="isLoading()" class="d-flex justify-content-center align-items-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
</div>

<!-- Error State -->
<div *ngIf="!isLoading() && errorMessage()" class="alert alert-danger">
  <i class="bi bi-exclamation-triangle me-2"></i>
  {{ errorMessage() }}
  <button class="btn btn-link p-0 ms-2" (click)="goBack()">Return to Field Tests</button>
</div>

<!-- Content -->
<ng-container *ngIf="!isLoading() && !errorMessage() && test()">
  <!-- Page Header -->
  <div class="d-flex justify-content-between align-items-start mb-4">
    <div>
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb mb-1">
          <li class="breadcrumb-item"><a routerLink="/logbook/testing/field">Field Tests</a></li>
          <li class="breadcrumb-item active">{{ test()!.title }}</li>
        </ol>
      </nav>
      <h1 class="h2 mb-0 fw-bold">{{ test()!.title }}</h1>
    </div>
    <div class="btn-group">
      <button class="btn btn-outline-secondary" (click)="goBack()">
        <i class="bi bi-arrow-left me-2"></i>Back
      </button>
      <button class="btn btn-outline-primary" (click)="editTest()">
        <i class="bi bi-pencil me-2"></i>Edit
      </button>
      <button class="btn btn-outline-danger" (click)="confirmDelete()">
        <i class="bi bi-trash me-2"></i>Delete
      </button>
    </div>
  </div>

  <div class="row g-4">
    <!-- Left Column - Main Content -->
    <div class="col-lg-8">
      <!-- Test Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Test Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Test Type</label>
              <p class="mb-0">
                <i class="me-2" [ngClass]="getTestTypeIcon(test()!.testType)"></i>
                {{ getTestTypeLabel(test()!.testType) }}
              </p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Date</label>
              <p class="mb-0">
                <i class="bi bi-calendar me-2"></i>
                {{ formatDate(test()!.date) }}
              </p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Aircraft</label>
              <p class="mb-0">
                <ng-container *ngIf="test()!.aircraft; else noAircraft">
                  <i class="icon-drone me-2"></i>
                  {{ test()!.aircraft }}
                </ng-container>
                <ng-template #noAircraft>
                  <span class="text-muted fst-italic">Not specified</span>
                </ng-template>
              </p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Performed By</label>
              <p class="mb-0">
                <ng-container *ngIf="test()!.pilot; else noPilot">
                  <i class="bi bi-person me-2"></i>
                  {{ test()!.pilot }}
                </ng-container>
                <ng-template #noPilot>
                  <span class="text-muted fst-italic">Not specified</span>
                </ng-template>
              </p>
            </div>
            <div class="col-12">
              <label class="form-label text-muted small mb-1">Location</label>
              <p class="mb-0">
                <ng-container *ngIf="test()!.location; else noLocation">
                  <i class="bi bi-geo-alt me-2"></i>
                  {{ test()!.location }}
                </ng-container>
                <ng-template #noLocation>
                  <span class="text-muted fst-italic">Not specified</span>
                </ng-template>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Checklist -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><i class="bi bi-list-check me-2"></i>Checklist Results</h5>
            <span class="badge" [ngClass]="getStatusClass(test()!.status)">
              <i class="bi me-1" [ngClass]="getStatusIcon(test()!.status)"></i>
              {{ test()!.status | titlecase }}
            </span>
          </div>
        </div>
        <div class="card-body p-0">
          <!-- Progress bar -->
          <div class="p-3 border-bottom" *ngIf="totalItems() > 0">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="small">
                <span class="text-success fw-medium">{{ passedCount() }} passed</span>
                <span class="text-muted mx-2">|</span>
                <span class="text-danger fw-medium">{{ failedCount() }} failed</span>
              </span>
              <span class="small text-muted">{{ passPercentage() }}% complete</span>
            </div>
            <div class="progress h-8px">
              <div class="progress-bar bg-success" [style.width.%]="passPercentage()"></div>
            </div>
          </div>

          <!-- Checklist items -->
          <div class="list-group list-group-flush">
            <div *ngFor="let item of test()!.checklist; let i = index"
                 class="list-group-item d-flex align-items-start gap-3 py-3">
              <!-- Status icon -->
              <div class="flex-shrink-0">
                <span class="d-flex align-items-center justify-content-center rounded-circle size-28px"
                      [class.bg-success]="item.passed"
                      [class.bg-danger]="!item.passed"
                      [class.text-white]="true">
                  <i class="bi" [class.bi-check]="item.passed" [class.bi-x]="!item.passed"></i>
                </span>
              </div>

              <!-- Item content -->
              <div class="flex-grow-1">
                <div class="fw-medium">{{ item.item }}</div>
                <div *ngIf="item.notes" class="text-muted small mt-1">
                  <i class="bi bi-chat-left-text me-1"></i>{{ item.notes }}
                </div>
              </div>

              <!-- Status badge -->
              <div class="flex-shrink-0">
                <span class="badge" [class.bg-success]="item.passed" [class.bg-danger]="!item.passed">
                  {{ item.passed ? 'Passed' : 'Failed' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div *ngIf="totalItems() === 0" class="text-center py-4">
            <i class="bi bi-list-check text-muted fs-2-5rem"></i>
            <p class="text-muted mt-2 mb-0">No checklist items</p>
          </div>
        </div>
      </div>

      <!-- Notes -->
      <div class="card mb-4 mb-lg-0">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-chat-left-text me-2"></i>Notes</h5>
        </div>
        <div class="card-body">
          <p class="mb-0" *ngIf="test()!.notes; else noNotes">{{ test()!.notes }}</p>
          <ng-template #noNotes>
            <p class="mb-0 text-muted fst-italic">No notes</p>
          </ng-template>
        </div>
      </div>
    </div>

    <!-- Right Column - Sidebar -->
    <div class="col-lg-4">
      <div class="sticky-top sticky-sidebar">
      <!-- Status Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0">Test Status</h5>
        </div>
        <div class="card-body text-center">
          <div class="mb-3">
            <span class="d-inline-flex align-items-center justify-content-center rounded-circle size-80px"
                  [ngClass]="{
                    'bg-success': test()!.status === 'passed',
                    'bg-danger': test()!.status === 'failed',
                    'bg-warning': test()!.status === 'pending'
                  }">
              <i class="bi text-white fs-2-5rem"
                 [ngClass]="getStatusIcon(test()!.status)"></i>
            </span>
          </div>
          <h4 class="mb-1" [ngClass]="{
            'text-success': test()!.status === 'passed',
            'text-danger': test()!.status === 'failed',
            'text-warning': test()!.status === 'pending'
          }">
            {{ test()!.status | titlecase }}
          </h4>
          <p class="text-muted small mb-0">
            {{ passedCount() }} of {{ totalItems() }} items passed
          </p>
        </div>
      </div>

      <!-- Quick Stats -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0">Quick Stats</h5>
        </div>
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-muted">Total Items</span>
            <span class="fw-medium">{{ totalItems() }}</span>
          </div>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-muted">Passed</span>
            <span class="fw-medium text-success">{{ passedCount() }}</span>
          </div>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-muted">Failed</span>
            <span class="fw-medium text-danger">{{ failedCount() }}</span>
          </div>
          <div class="d-flex justify-content-between align-items-center">
            <span class="text-muted">Pass Rate</span>
            <span class="fw-medium">{{ passPercentage() }}%</span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0">Actions</h5>
        </div>
        <div class="card-body">
          <div class="d-grid gap-2">
            <button class="btn btn-outline-primary" (click)="editTest()">
              <i class="bi bi-pencil me-2"></i>Edit Test
            </button>
            <button class="btn btn-outline-danger" (click)="confirmDelete()">
              <i class="bi bi-trash me-2"></i>Delete Test
            </button>
            <button class="btn btn-outline-secondary" (click)="goBack()">
              <i class="bi bi-arrow-left me-2"></i>Back to Field Tests
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</ng-container>

<!-- Confirm Delete Modal -->
<app-confirm-modal
  #confirmModal
  (confirmed)="onDeleteConfirmed()"
  (cancelled)="onDeleteCancelled()"
></app-confirm-modal>
